import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import { CanvasAlignment } from '@/components/canvas/constants'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useDualCanvasSize } from '@/hooks/useDualCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import {
  useGenerateRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  ImageCalibrationStatus,
  RecipeGenerationStatus,
  setImageCalibrationStatus,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import {
  removeCrownRecipeKey,
  setCrownRecipe,
  setSelectedCrownKey,
} from '@/store/stainingStudio'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  getProtocolValue,
  hasNonEmptyOutputDualCanvas,
  isImageCalibrated,
  saveRecipeGeneric,
} from '@/utils/helper'
import { buildStainingWithEnamelPayload } from '@/utils/matisseV2/stainingWithEnamel'

import { CROWN_BUTTON_WITH_COLOR } from '../utils/constants'
import KonvaCrown from './KonvaCrown'
import StaingingStudio from './StaingingStudio'

const togglePlacement = (
  currentPlacement: string,
): PLACEMENT.LEFT | PLACEMENT.RIGHT =>
  currentPlacement === PLACEMENT.LEFT ? PLACEMENT.RIGHT : PLACEMENT.LEFT

const Crown = () => {
  const { t } = useTranslation()
  const alignment = CanvasAlignment.VERTICAL
  const dispatch = useDispatch()
  const chipsContainerRef = useRef<HTMLDivElement | null>(null)
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipMessage, setTooltipMessage] = useState('')
  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [circleColor, setCircleColor] = useState<string>('#ffffff')
  const [activeCanvas, setActiveCanvas] = useState<PLACEMENT | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<
    keyof typeof CROWN_BUTTON_WITH_COLOR | null
  >('Base Color')

  const [generateRecipe] = useGenerateRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const selectedColor = useAppSelector((store) => store.app.selectedColor)
  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )
  const greyCardValue = useAppSelector((state) => state.app.greyCardValue)
  const newImages = useAppSelector((state) => state.app.newImages)
  const placement = useAppSelector((state) => state.app.placement)
  const selectedCrownKey = useAppSelector(
    (state) => state.stainingStudio.selectedCrownKey,
  )
  const crownRecipe = useAppSelector(
    (state) => state.stainingStudio.crownRecipe,
  )
  const selectedCrownStainingBrand = useAppSelector(
    (state) => state.stainingStudio.selectedCrownStainingBrand,
  )
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const handleDeleteCrown = useCallback(
    (crownKey: string) => {
      if (!dualCanvasImage) return

      dispatch(setSelectedCrownKey(null))
      dispatch(removeCrownRecipeKey({ key: crownKey }))
    },
    [dispatch, dualCanvasImage],
  )

  const [leftElements, rightElements] = useMemo(() => {
    if (!crownRecipe || !selectedCategory) return [[], []]

    const allowedKeys = Object.keys(
      CROWN_BUTTON_WITH_COLOR[selectedCategory] || {},
    )

    const createElements = (side: PLACEMENT.LEFT | PLACEMENT.RIGHT) =>
      Object.entries(crownRecipe?.[side] || {})
        .filter(([key]) => allowedKeys.includes(key))
        .map(([key, recipe]) => ({
          component: (
            <KonvaCrown
              key={key}
              input={recipe.input}
              output={recipe.output}
              onDelete={handleDeleteCrown}
              tooltipPlacement={side}
            />
          ),
          id: recipe.input?.fullmonolithiceffect,
        }))

    return [createElements(PLACEMENT.LEFT), createElements(PLACEMENT.RIGHT)]
  }, [crownRecipe, selectedCategory, handleDeleteCrown])

  const { containerRef, dimensions } = useDualCanvasSize(
    alignment,
    chipsContainerRef.current?.clientHeight,
  )

  const isBothSideImageSelected =
    !!dualCanvasImage?.[PLACEMENT.LEFT]?.id &&
    !!dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const isLeftImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.LEFT],
  )
  const isRightImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.RIGHT],
  )
  const isBothImageCalibrated = isLeftImageCalibrated && isRightImageCalibrated

  const calibrateImageIfNeeded = (
    type: string,
    currentPlacement: PLACEMENT.LEFT | PLACEMENT.RIGHT,
  ) => {
    const imageId = dualCanvasImage?.[currentPlacement]?.id

    if (
      !imageId ||
      type !== imageType.RAW ||
      imageCalibrationStatus?.[imageId] !== ImageCalibrationStatus.CALIBRATING
    ) {
      return
    }

    dispatch(
      setImageCalibrationStatus({
        id: imageId,
        status: ImageCalibrationStatus.CALIBRATED,
      }),
    )
  }

  const hasTargetColor = (placementToCheck: PLACEMENT, key: string) => {
    const recipeByPlacement =
      crownRecipe?.[placementToCheck as keyof typeof crownRecipe]

    return !isEmpty(recipeByPlacement?.[key]?.input?.targetcolor)
  }

  const startFirstStep = (
    type: string,
    currentPlacement: PLACEMENT.LEFT | PLACEMENT.RIGHT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedCrownKey) return

    const imageId = dualCanvasImage?.[currentPlacement]?.id
    const toggledPlacement = togglePlacement(currentPlacement)

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATE))

    const payload = {
      caseId,
      fullmonolithiceffect: selectedCrownKey,
      protocol: getProtocolValue(dualCanvasImage?.[currentPlacement]),
      stainingbrand: selectedCrownStainingBrand || '',
      targetcolor: [
        selectedColor.L,
        selectedColor.a,
        selectedColor.b,
      ] as string[],
      crownPlacement: toggledPlacement,
    }

    setTooltipOpen(true)
    setTooltipMessage(t('stainingCorrection.panel.secondStep'))

    dispatch(
      setCrownRecipe({
        placement: currentPlacement,
        key: selectedCrownKey,
        data: {
          input: { ...payload, x: elementPos?.x, y: elementPos?.y },
          output: {},
        },
      }),
    )
  }

  const startSecondStep = (
    type: string,
    currentPlacement: PLACEMENT.LEFT | PLACEMENT.RIGHT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedCrownKey) return

    const imageId = dualCanvasImage?.[currentPlacement]?.id
    const toggledPlacement = togglePlacement(currentPlacement)

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATING))

    const payload = buildStainingWithEnamelPayload({
      backgroundcolor: [selectedColor.L, selectedColor.a, selectedColor.b],
      fullmonolithiceffect: selectedCrownKey,
      protocol: getProtocolValue(dualCanvasImage?.[currentPlacement]),
      stainingbrand: selectedCrownStainingBrand || '',
      targetcolor:
        crownRecipe?.[toggledPlacement as keyof typeof crownRecipe]?.[
          selectedCrownKey
        ]?.input?.targetcolor,
    })

    toast.info(t('recipeGenerate.inProgress'))

    generateRecipe({
      caseId,
      recipePath: 'staining-fullmonolithic',
      data: payload,
    })
      .unwrap()
      .then((res) => {
        const { generation_id, ...output } = res ?? {}
        setTooltipOpen(false)
        dispatch(
          setCrownRecipe({
            placement: currentPlacement,
            key: selectedCrownKey,
            data: {
              input: {
                ...{
                  ...payload,
                  targetcolor: [],
                },
                x: elementPos?.x,
                y: elementPos?.y,
              } as any,
              output,
              generation_id,
            },
          }),
        )
        dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATED))
        dispatch(setSelectedCrownKey(null))
        setTooltipOpen(false)
        toast.success(t('stainingCorrection.panel.recipeGenerated'))
      })
      .catch((error) => {
        toast.error(
          error?.data?.message ||
            t('stainingCorrection.panel.recipeGenerationFailed'),
        )
      })
  }

  const handleRecipeGeneration = (
    type: string,
    currentPlacement: PLACEMENT.LEFT | PLACEMENT.RIGHT,
    elementPos?: { x: number; y: number },
  ) => {
    if (!selectedCrownKey) {
      return
    }

    const toggledPlacement = togglePlacement(currentPlacement)

    const hasCurrentTarget = hasTargetColor(currentPlacement, selectedCrownKey)
    const hasToggledTarget = hasTargetColor(toggledPlacement, selectedCrownKey)

    if (!hasCurrentTarget && !hasToggledTarget) {
      startFirstStep(type, currentPlacement, elementPos)
      return
    }

    if (!hasCurrentTarget) {
      startSecondStep(type, currentPlacement, elementPos)
    }
  }

  const onImageClick = (
    type: string,
    currentPlacement: PLACEMENT.LEFT | PLACEMENT.RIGHT,
    elementPos?: { x: number; y: number },
  ) => {
    const imageId = dualCanvasImage?.[currentPlacement]?.id

    if (!imageId) {
      return
    }

    calibrateImageIfNeeded(type, currentPlacement)

    if (!isBothImageCalibrated) {
      return
    }

    if (!selectedCrownStainingBrand) {
      toast.error(t('stainingCorrection.panel.selectValidStainingBrand'))
      return
    }

    handleRecipeGeneration(type, currentPlacement, elementPos)
  }

  const onSaveAsImage = () => {
    generateImage(
      combinedRef as React.RefObject<HTMLCanvasElement | HTMLDivElement>,
      'Crown-Recipe.png',
    )
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      dualCanvasImage,
      recipeName,
      output: crownRecipe,
      workflow: Workflows?.STAINING_STUDIO,
      mutationFn: saveRecipe,
      recipeType: 'staining_fullmonolithic',
      closeDialog: () => setSaveRecipedialogOpen(false),
      inputType: 'dual',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      dualCanvasImage,
      recipeName,
      output: crownRecipe,
      workflow: Workflows.STAINING_STUDIO,
      mutationFn: savePdf,
      recipeType: 'staining_fullmonolithic',
      inputType: 'dual',
      isPdf: true,
    })
  }

  return (
    <CaseInfoLayout
      disabled={hasNonEmptyOutputDualCanvas(crownRecipe)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box display="flex" flexGrow={1} ref={containerRef} position="relative">
          {!imageEdit ? (
            <>
              <Box ref={combinedRef} display="flex">
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.LEFT]) ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick as any}
                  placement={PLACEMENT.LEFT}
                  type={dualCanvasImage?.[PLACEMENT.LEFT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.LEFT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={leftElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_height}
                  isActiveCircle={!!selectedCrownKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick as any}
                  placement={PLACEMENT.RIGHT}
                  type={dualCanvasImage?.[PLACEMENT.RIGHT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.RIGHT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={rightElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_height}
                  isActiveCircle={!!selectedCrownKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
              </Box>
              {isBothSideImageSelected && isBothImageCalibrated && (
                <StaingingStudio
                  setTooltipOpen={setTooltipOpen}
                  setTooltipMessage={setTooltipMessage}
                  dimensions={dimensions}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory as any}
                />
              )}
              {containerRef.current && (
                <SnackbarTooltip
                  containerRef={containerRef}
                  message={tooltipMessage}
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  placement="top"
                  offset={[0, -100]}
                  autoHideDuration={10000}
                  maxWidth={500}
                />
              )}
            </>
          ) : (
            <ImageEdit
              selectedImage={
                placement === PLACEMENT.LEFT
                  ? dualCanvasImage[PLACEMENT.LEFT]
                  : dualCanvasImage[PLACEMENT.RIGHT]
              }
            />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default Crown
